<script setup lang="ts">
import type { BreadcrumbProps } from '#seo-utils/app/composables/useBreadcrumbItems'
import { SolrParamsKey } from '#shared/solr'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import ControlButton from '~/components/ControlButton.vue'
import { useAuthorQuery } from '~/composables/author/useAuthorQuery'

const route = useRoute<'author'>()

const offset = useHeaderHeight()

const biographyExpanded = ref<boolean>(false)

const biographyRef = ref<HTMLElement | null>(null)
useContentInternalLinks(biographyRef)

const { data: authorData, suspense, isLoading } = useAuthorQuery(computed(() => ({
  [SolrParamsKey.Author]: route.params.slug,
})))

if (import.meta.server) {
  await suspense()
}

const author = computed(() => {
  const doc = authorData.value?.response.docs[0]
  return {
    ...doc,
    name: doc?.name ?? '',
    bio: doc?.bio ?? '',
    education: doc?.education ?? '',
    image: doc?.image ?? '',
  }
})

const authorId = computed(() => author.value?.ezObjectId)
const authorName = computed(() => author.value?.name)
const { data: worksData } = useAuthorWorksQuery(authorId, authorName)

const allWorks = computed(() => worksData.value?.response.docs ?? [])
const books = computed(() => allWorks.value.filter(work => work.nodeType === 'book'))
const academicArticles = computed(() => allWorks.value.filter(work => work.nodeType === 'academic_article'))
const journalArticles = computed(() => allWorks.value.filter(work => ['journal_article', 'journal_editorial'].includes(work.nodeType)))

const MAX_BIO_LENGTH = 800

const isBioTruncated = computed(() => (author.value?.bodytext?.length ?? 0) > MAX_BIO_LENGTH)

const title = computed(() => author.value?.name ?? route.params.slug?.replace('-', ' ') ?? 'Forfatter')

const breadcrumbOverrides = computed(() => {
  return [
    undefined,
    {
      label: 'Forfattere',
      to: '/forfattere',
    },
    {
      label: title.value,
      to: route.path,
    },
  ] satisfies BreadcrumbProps['overrides']
})

useTrackLastActivity(title)

useHead({
  bodyAttrs: {
    class: 'bg-white',
  },
  title,
})

definePageMeta({
  colorMode: 'light',
  theme: 'moss',
  layout: 'author',
  name: 'author',
})
</script>

<template>
  <ContentDiv class="bg-white">
    <Breadcrumbs :overrides="breadcrumbOverrides" />

    <div class="flex flex-col gap-4xl max-w-screen-xl mx-auto pb-4xl pt-xl">
      <div class="flex flex-col md:flex-row gap-l xl:gap-5xl relative">
        <div data-sticky-container-card class="h-fit self-center md:self-start z-10 md:sticky">
          <AuthorSkeleton v-if="isLoading" size="large" />
          <AuthorCard
            v-else
            :name="author.name"
            :bio="author.bio"
            :education="author.education"
            :image="author.image"
            size="large"
            presentation
          />
          <span class="text-xs text-secondary">© Illustrasjoner av Illustratør / byHands</span>
        </div>

        <div class="grow flex flex-col gap-xl pb-10">
          <h1 class="title-xl">
            <Skeleton v-if="isLoading" class="size-full bg-carbon-200 rounded" />
            <template v-else>
              {{ author.name }}
            </template>
          </h1>

          <div v-if="isLoading" class="flex flex-col gap-5">
            <Skeleton
              v-for="n in 10"
              :key="n"
              class="bg-carbon-200 rounded h-3"
              :style="{
                // Between 60% and 100%
                width: `${Math.floor(Math.random() * (100 - 60 + 1)) + 60}%`,
              }"
            />
          </div>
          <div v-else>
            <div ref="biographyRef" class="author-full-biography overflow-hidden relative transition-[height] duration-1000" :class="isBioTruncated && !biographyExpanded ? 'h-[400px]' : 'h-fit expanded'" v-html="author.bodytext" />
            <div v-if="isBioTruncated" class="flex items-center gap-2xs mt-s">
              <ControlButton :aria-label="biographyExpanded ? 'Vis mindre' : 'Vis mer'" :icon="biographyExpanded ? faChevronUp : faChevronDown" type="outlined" :aria-expanded="biographyExpanded" class="text-primary" @click="biographyExpanded = !biographyExpanded" />
              <span class="text-xsmall">{{ biographyExpanded ? 'Vis mindre' : 'Vis mer' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2xl">
        <div v-if="isLoading" class="flex gap-s flex-col sm:flex-row sm:flex-wrap">
          <Skeleton v-for="n in 4" :key="n" class="size-full bg-carbon-200 rounded-4xl h-[419px] w-[358px] sm:w-[307px]" />
        </div>
        <template v-else>
          <AuthorWorksSection v-if="academicArticles.length" title="Fagartikler" :works="academicArticles" />
          <AuthorWorksSection v-if="books.length" title="Fagbøker" :works="books" />
          <AuthorWorksSection v-if="journalArticles.length" title="Tidsskriftartikler" :works="journalArticles" />
        </template>
      </div>
    </div>
  </ContentDiv>
</template>

<style scoped lang="postcss">
[data-sticky-container-card] {
  top: calc(v-bind(offset) + theme('spacing.s'));
}

.author-full-biography::after {
  @apply content-[''] absolute inset-0 top-auto h-24 pointer-events-none transition-opacity duration-500;
  background: linear-gradient(to bottom, transparent 0%, white 100%);
}

.author-full-biography.expanded::after {
  @apply opacity-0;
}

:deep(.author-full-biography) {
  @apply flex flex-col gap-xs;

  h2,
  h4 {
    @apply text-3xl font-normal pt-m;
  }

  .leadParagraph {
    @apply font-heading text-3xl pb-l;
  }

  p {
    @apply text-primary dark:text-secondary-on_dark;
  }

  ul {
    @apply list-disc pl-l;
  }
}
</style>
